import {
  IErrorApiResponse,
  IPatchObject,
  ISuccessApiResponse,
  IValidationError,
} from '@wyz/types';
import { restrictApi, useMutation, UseMutationOptions } from '@wyz/utils';
import { useApi, useConfig } from '~/common/context';
import { IAutomakerGroup } from '~/common/types';
import { ConnectQueryKeys } from '~/connect/api';
import { ICriteriaValueUsageError } from '~/connect/types';

type Options = {
  patch?: UseMutationOptions<
    ISuccessApiResponse<IAutomakerGroup>,
    IErrorApiResponse<IValidationError, string, ICriteriaValueUsageError>,
    IPatchObject<Pick<IAutomakerGroup, 'status' | 'name'>>
  >;
};

export const useManageAutomakerGroups = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { callCoreApi } = useApi();
  const { tenantCode } = useConfig();

  const patchMutation = useMutation({
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse<IAutomakerGroup>>(
        `/tenants/api/wtb/tenants/${tenantCode}/automaker-groups/${variables.id}`,
        {
          body: JSON.stringify(variables.body),
          method: 'PATCH',
        },
      ),
    keysToInvalidateOnSuccess: [
      ConnectQueryKeys.automakerGroups.base(),
      {
        key: ConnectQueryKeys.criteria.all({ includeInternal: false }),
        type: 'all',
      },
      {
        key: ConnectQueryKeys.criteria.all({ includeInternal: true }),
        type: 'all',
      },
    ],
    ...options.patch,
  });

  return restrictApi(options, { patch: patchMutation });
};
